/***
 *
 *   FYI SETTINGS PAYLOAD
 *   Reads a stored FYI row into the settings form's PDF values, and turns the
 *   form's answers into the body POST /api/integration/fyi/create expects.
 *
 *   The server merges that body over the stored row (helper/fyiPdfSettings.js,
 *   mergeFyiPdfSettings): a key the page sends wins, a key it leaves out keeps
 *   its stored value. So what this file leaves out matters as much as what it
 *   sends.
 *
 *   Plain ESM with no imports, on purpose. The server's mocha suite
 *   (test/fyi-pdf-settings.js, "client drift") require()s this file through
 *   Node's require(esm) to pin the option keys and defaults to the server
 *   registry. A bare import (a package, `components/lib`) or an extensionless
 *   relative one breaks that, so keep it import-free, or import same-folder
 *   files with their `.js` extension.
 *
 **********/

// The three UI-settable PDF options, in the dashboard's Download PDF order and
// with its defaults. `key` is the server's pdf_writeback_options key and
// `question` the flat question in fyi_settings.js, which holds the labels.
// Proof of address is backend-only (R4) and deliberately absent: no save from
// this page can change it.
export const FYI_PDF_WRITEBACK_OPTIONS = Object.freeze(
  [
    { key: "include_images", question: "pdf_include_images", defaultValue: true },
    { key: "include_sanctions", question: "pdf_include_sanctions", defaultValue: false },
    {
      key: "include_adverse_media",
      question: "pdf_include_adverse_media",
      defaultValue: false,
    },
  ].map((option) => Object.freeze(option))
);

function asMap(m) {
  return m && typeof m === "object" && !Array.isArray(m) ? m : {};
}

// A blank cabinet is sent as null, which the server reads as "clear it". The
// same reading as the server's normalizeCabinetId.
function cabinetIdForSave(value) {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

// The form values for the PDF switch and its three options, from a stored
// row's `data`. GET /api/integration/fyi already normalizes the block (a
// legacy row reads as images on, the rest off), so this only restates the
// defaults for a key that is missing or not a real boolean — and always
// returns a boolean, because setValue(name, undefined) would wipe the
// question's default instead of keeping it.
export function fyiPdfFormValues(storedData) {
  const data = asMap(storedData);
  const block = asMap(data.pdf_writeback_options);
  const values = { pdf_results_writeback: data.pdf_results_writeback === true };
  for (const option of FYI_PDF_WRITEBACK_OPTIONS) {
    values[option.question] =
      typeof block[option.key] === "boolean" ? block[option.key] : option.defaultValue;
  }
  return values;
}

// The save body for the FYI settings form's sender.data.
//
// SurveyJS clears visibleIf-hidden questions before onComplete, so with the PDF
// toggle off the options and cabinet are already gone from sender.data. That
// absence has to reach the server as absence — reading it as false or null is
// what wiped stored settings before (docs/solutions/logic-errors/
// surveyjs-visibleif-gating-wipes-stored-settings-on-save.md). So the toggle's
// visibleIf is restated here rather than trusted:
//
// - Toggle on: send the three options as real booleans, and cabinet_id. A
//   blank cabinet goes as null, because SurveyJS deletes an emptied question's
//   key and an absent cabinet_id would keep the stored one; without the null
//   an admin could never clear it.
// - Toggle off: send neither, and pdf_results_writeback: false, so the merge
//   keeps what is stored (AE6).
export function buildFyiSettingsPayload(formData) {
  const form = asMap(formData);
  const data = { ...form };

  // feature 007: nest the flat T2 toggle into the tranche2 block (field mapping
  // is backend-configurable via tranche2.field_ids).
  data.tranche2 = { enabled: form.tranche2_enabled === true };
  delete data.tranche2_enabled;

  // The flat questions never reach the server, and the block is always built
  // here from the registry, so nothing else can slip proof of address into it.
  for (const option of FYI_PDF_WRITEBACK_OPTIONS) delete data[option.question];
  delete data.pdf_writeback_options;
  delete data.cabinet_id;

  const pdfWritebackOn = form.pdf_results_writeback === true;
  data.pdf_results_writeback = pdfWritebackOn;
  if (!pdfWritebackOn) return data;

  data.pdf_writeback_options = Object.fromEntries(
    FYI_PDF_WRITEBACK_OPTIONS.map((option) => [option.key, form[option.question] === true])
  );
  data.cabinet_id = cabinetIdForSave(form.cabinet_id);
  return data;
}
